<template>
    <PageHeader :title="$t('setup_detail.title')" @back="goBack" />

    <div class="w-screen flex-1 space-y-10 overflow-y-auto p-4">
        <!-- Hero Section with Setup Name and Stats -->
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <SetupCard
                :setup="setup"
                :setup-stats="setupStats"
                :selected-game-type="null"
                :selected="true"
            />

            <!-- Action Buttons -->
            <div class="flex justify-between gap-3 md:flex-col">
                <DiagonalCutButton
                    variant="primary"
                    cut-position="right-top"
                    :full-width="true"
                    data-testid="setup-detail-edit"
                    @click="editSetup"
                >
                    <span class="text-lg font-black"> {{ $t('setup_detail.edit') }} </span>
                </DiagonalCutButton>
                <!-- <DiagonalCutButton
                    variant="secondary"
                    cut-position="left-top"
                    :full-width="true"
                    fillColor="max-md:bg-white"
                    @click="shareSetup"
                >
                    <span class="max-md:text-bg text-lg font-black"> SHARE SETUP </span>
                </DiagonalCutButton> -->
            </div>
        </div>

        <!-- Dart Components Grid -->
        <div v-if="setup.dart" class="flex w-full items-center justify-center">
            <SetupProduct
                :part="setup.dart"
                :comment="setup.dart_comment"
                class="w-full max-w-md"
            ></SetupProduct>
        </div>

        <div
            v-else
            class="grid w-full grid-cols-[1fr_auto_1fr] grid-rows-[1fr_auto_1fr] gap-6 overflow-hidden"
        >
            <SetupProduct :part="setup.shaft" :comment="setup.shaft_comment"></SetupProduct>

            <div class="bg-card w-0.5"></div>

            <SetupProduct :part="setup.barrel" :comment="setup.barrel_comment"></SetupProduct>

            <div class="bg-card col-span-3 h-0.5 w-full"></div>

            <SetupProduct :part="setup.tip" :comment="setup.tip_comment"></SetupProduct>

            <div class="bg-card w-0.5"></div>
            <SetupProduct :part="setup.flight" :comment="setup.flight_comment"></SetupProduct>
        </div>

        <!-- Summary Statistics Cards -->
        <div class="grid grid-cols-3 gap-3">
            <Panel size="full" padding="p-3">
                <div class="flex flex-col items-center justify-center gap-3">
                    <div class="flex w-full flex-col items-center gap-2">
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 19 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                        >
                            <path
                                d="M18.4141 0V7.01465L18.1211 7.30664L15.2139 10.2139V15.0137L11.8145 18.4141L0 6.60059L3.39941 3.2002H8.2002L11.4004 0H18.4141ZM6.12109 13.707L2.12109 17.707L0.707031 16.293L4.70703 12.293L6.12109 13.707ZM8.52148 16.1074L6.9209 17.707L5.50684 16.293L7.10742 14.6924L8.52148 16.1074ZM10.8281 14.5996L11.8135 15.585L13.2139 14.1855V12.2139L10.8281 14.5996ZM5.22852 9L9.41406 13.1855L16.4141 6.18555V2H12.2285L5.22852 9ZM3.7207 11.3076L2.12109 12.9072L0.707031 11.4932L2.30664 9.89258L3.7207 11.3076ZM13.7334 6.09473L12.1084 7.71973L10.6943 6.30566L12.3193 4.68066L13.7334 6.09473ZM2.82812 6.59961L3.81445 7.58594L6.2002 5.2002H4.22852L2.82812 6.59961Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                        <span class="block w-full truncate text-center text-sm font-bold uppercase">{{ $t('setup_detail.wins') }}</span>
                    </div>
                    <div class="text-2xl leading-none font-extrabold text-white">{{ totalWins }}</div>
                </div>
            </Panel>
            <Panel size="full" padding="p-3">
                <div class="flex flex-col items-center justify-center gap-3">
                    <div class="flex w-full flex-col items-center gap-2">
                        <svg
                            width="14"
                            height="11"
                            viewBox="0 0 14 11"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                        >
                            <path
                                d="M13.4141 1.41406L4.70703 10.1211L0 5.41406L1.41406 4L4.70703 7.29297L12 0L13.4141 1.41406Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                        <span class="block w-full truncate text-center text-sm font-bold uppercase">{{ $t('setup_detail.winrate') }}</span>
                    </div>
                    <div class="text-2xl leading-none font-extrabold text-white">
                        {{ totalWinrate }}%
                    </div>
                </div>
            </Panel>
            <Panel size="full" padding="p-3">
                <div class="flex flex-col items-center justify-center gap-3">
                    <div class="flex w-full flex-col items-center gap-2">
                        <svg
                            width="19"
                            height="19"
                            viewBox="0 0 19 19"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="h-5 w-5"
                        >
                            <path
                                d="M18.4141 0V7.01465L18.1211 7.30664L15.2139 10.2139V15.0137L11.8145 18.4141L0 6.60059L3.39941 3.2002H8.2002L11.4004 0H18.4141ZM6.12109 13.707L2.12109 17.707L0.707031 16.293L4.70703 12.293L6.12109 13.707ZM8.52148 16.1074L6.9209 17.707L5.50684 16.293L7.10742 14.6924L8.52148 16.1074ZM10.8281 14.5996L11.8135 15.585L13.2139 14.1855V12.2139L10.8281 14.5996ZM5.22852 9L9.41406 13.1855L16.4141 6.18555V2H12.2285L5.22852 9ZM3.7207 11.3076L2.12109 12.9072L0.707031 11.4932L2.30664 9.89258L3.7207 11.3076ZM13.7334 6.09473L12.1084 7.71973L10.6943 6.30566L12.3193 4.68066L13.7334 6.09473ZM2.82812 6.59961L3.81445 7.58594L6.2002 5.2002H4.22852L2.82812 6.59961Z"
                                fill="#FF2C2C"
                            />
                        </svg>
                        <span class="block w-full truncate text-center text-sm font-bold uppercase">{{ $t('setup_detail.avg') }}</span>
                    </div>
                    <div class="text-2xl leading-none font-extrabold text-white">
                        {{ totalAverage }}
                    </div>
                </div>
            </Panel>
        </div>

        <!-- Games Played Graph -->
        <Panel size="full" class="p-5" v-if="false">
            <div class="mb-4 flex items-center justify-between">
                <h2 class="font-title text-sm font-bold uppercase">Games played</h2>
                <div class="text-sm font-bold text-white">{{ totalGames }}</div>
            </div>
            <BarGraph
                v-if="gamesPerMonth.length > 0"
                :data="gamesPerMonth"
                :labels="monthLabels"
                :autorange="true"
                :show-average="true"
                :min="0"
            />
            <div v-else class="flex items-center justify-center py-8">
                <div class="text-center">
                    <div class="mb-2 text-4xl">📊</div>
                    <div class="text-sm text-gray-400">{{ $t('setup_detail.no_games') }}</div>
                </div>
            </div>
        </Panel>

        <DiagonalCutButton
            variant="primary"
            :full-width="true"
            data-testid="setup-detail-delete"
            @click="showDeleteConfirmation = true"
        >
            <span class="flex justify-center gap-4 text-lg font-black">
                {{ $t('setup_detail.delete') }}
                <svg viewBox="0 0 14 18" fill="none" xmlns="http://www.w3.org/2000/svg" class="h-5">
                    <path
                        d="M7.2002 0C8.31403 5.17723e-05 9.38231 0.442862 10.1699 1.23047C10.6675 1.72808 11.0271 2.33766 11.2246 3H14V5H13V18H1.40039V5H0V3H3.17578C3.37332 2.33761 3.73284 1.72809 4.23047 1.23047C5.01812 0.442816 6.08629 0 7.2002 0ZM3.40039 16H11V5.2002H3.40039V16ZM6.60059 7.40039V13.7998H4.60059V7.40039H6.60059ZM9.7998 7.40039V13.7998H7.7998V7.40039H9.7998ZM7.2002 2C6.61672 2 6.05711 2.23195 5.64453 2.64453C5.53554 2.75352 5.43926 2.87277 5.35645 3H9.04297C8.96024 2.87294 8.86469 2.75339 8.75586 2.64453C8.34333 2.232 7.7836 2.00005 7.2002 2Z"
                        fill="white"
                    />
                </svg>
            </span>
        </DiagonalCutButton>

        <div
            v-if="showDeleteConfirmation"
            class="fixed inset-0 z-50 flex items-center justify-center bg-black/65"
            @click.self="showDeleteConfirmation = false"
        >
            <div class="bg-bg border-stroke-1 mx-4 w-full max-w-md border shadow-xl">
                <div class="p-4">
                    <div class="mb-6 text-center">
                        <h2 class="font-title mb-2 text-2xl font-bold text-white uppercase">
                            {{ $t('setup_detail.confirm_delete_title') }}
                        </h2>
                        <p class="text-sm text-gray-400">
                            {{ $t('setup_detail.confirm_delete_body') }}
                        </p>
                    </div>
                </div>

                <div class="border-stroke-1 mt-4 grid grid-cols-2 gap-4 border-t p-4">
                    <DiagonalCutButton
                        variant="secondary"
                        cut-position="right-top"
                        data-testid="setup-delete-cancel"
                        @click="showDeleteConfirmation = false"
                    >
                        {{ $t('general.cancel') }}
                    </DiagonalCutButton>
                    <DiagonalCutButton
                        variant="primary"
                        cut-position="left-top"
                        data-testid="setup-delete-confirm"
                        @click="deleteSetup(setup)"
                        :disabled="isDeleting"
                    >
                        {{ $t('general.delete') }}
                    </DiagonalCutButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from '@/stores/auth.js'
import { useDartSetupStore } from '@/stores/dartSetup.js'
import PageHeader from '@/components/PageHeader.vue'
import Panel from '@/components/layout/Panel.vue'
import DiagonalCutButton from '@/components/ui/DiagonalCutButton.vue'
import BarGraph from '@/components/ui/graphs/BarGraph.vue'
import SetupCard from '@/components/ui/SetupCard.vue'
import SetupProduct from '@/components/ui/setups/SetupProduct.vue'

export default {
    name: 'SetupDetailView',
    components: {
        PageHeader,
        Panel,
        DiagonalCutButton,
        BarGraph,
        SetupCard,
        SetupProduct,
    },
    data() {
        return {
            showDeleteConfirmation: false,
            isDeleting: false,
        }
    },
    computed: {
        ...mapStores(useAuthStore, useDartSetupStore),
        setupId() {
            return this.$route.params.id
        },
        setup() {
            if (!this.authStore.dart_setups) return null
            return this.authStore.dart_setups.find((s) => s.id === this.setupId)
        },
        setupStats() {
            return this.authStore.statistics?.setups || {}
        },
        aggregatedStats() {
            const stats = this.setupStats[this.setupId]
            if (!stats) {
                return {
                    games_played: 0,
                    wins: 0,
                    winrate: 0,
                    average: 0,
                }
            }

            // Aggregate stats across all game types
            let totalGames = 0
            let totalWins = 0
            let totalScore = 0
            let totalDarts = 0

            Object.values(stats).forEach((gameTypeStats) => {
                totalGames += gameTypeStats.games_played || 0
                totalWins += gameTypeStats.wins || 0
                // Calculate weighted average
                if (gameTypeStats.games_played > 0) {
                    const avg = gameTypeStats.average || 0
                    const games = gameTypeStats.games_played
                    totalScore += avg * games
                    totalDarts += games
                }
            })

            const winrate = totalGames > 0 ? (totalWins / totalGames) * 100 : 0
            const average = totalDarts > 0 ? totalScore / totalDarts : 0

            return {
                games_played: totalGames,
                wins: totalWins,
                winrate: Math.round(winrate * 100) / 100,
                average: Math.round(average * 100) / 100,
            }
        },
        totalGames() {
            return this.aggregatedStats.games_played
        },
        totalWins() {
            return this.aggregatedStats.wins
        },
        totalWinrate() {
            return Math.floor(this.aggregatedStats.winrate)
        },
        totalAverage() {
            return Math.floor(this.aggregatedStats.average * 100) / 100
        },
        gamesPerMonth() {
            // For now, return placeholder data
            // TODO: Calculate actual monthly games from match history
            // This would require fetching matches and grouping by month
            return [6, 12, 8, 10] // Placeholder: FEB, MAR, APR, MAY
        },
        monthLabels() {
            // Get last 4 months
            const months = []
            const now = new Date()
            for (let i = 3; i >= 0; i--) {
                const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
                months.push(date.toLocaleDateString('en-US', { month: 'short' }).toUpperCase())
            }
            return months
        },
    },
    mounted() {
        if (!this.setup) {
            this.$router.push({ name: 'setups' })
        }
    },
    methods: {
        getImageUrl(imageUrl) {
            if (!imageUrl) return ''
            if (imageUrl.startsWith('http')) return imageUrl
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || ''
            const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '')
            return `${baseUrl}/${imageUrl}`
        },
        editSetup() {
            this.dartSetupStore.setEntryPoint({
                type: 'route',
                name: 'setup-detail',
                params: { id: this.setupId },
            })
            this.$router.replace({ name: 'setup-edit', params: { id: this.setupId } })
        },
        shareSetup() {
            // TODO: Implement share functionality
            this.$toast(this.$t('setup_detail.share_coming_soon'), 3000)
        },
        async deleteSetup(setup) {
            this.isDeleting = true
            try {
                await this.authStore.deleteSetup(setup.id)
                this.$router.push({ name: 'setups' })
                this.$toast(this.$t('setups.delete_success'), 3000)
            } catch (error) {
                console.error('Failed to delete setup:', error)
                const errorMessage =
                    error.response?.data?.message ||
                    error.message ||
                    this.$t('general.error_retry')
                this.$toast(this.$t('general.error_title'), 5000, errorMessage)
            } finally {
                this.isDeleting = false
            }
        },
        goBack() {
            const entryPoint = this.dartSetupStore.entryPoint
            this.dartSetupStore.clearEntryPoint()

            if (entryPoint?.type === 'route') {
                this.$router.replace({ name: entryPoint.name, params: entryPoint.params || {} })
            } else {
                this.$router.replace({ name: 'setups' })
            }
        },
    },
}
</script>
